/*!
 * Chrono Candy - "Sell your watch" section (3 steps: Watch, Photos, You).
 * Plain JavaScript, no frameworks, no build step. Works with any number of .ccsw[data-ccsw] blocks on a page.
 *
 * Submission:
 *   data-mode="endpoint": multipart POST to data-endpoint (Formspree or similar), Accept: application/json.
 *   data-mode="contact":  fills Shopify's own contact form (text only) and submits it; the page reloads with the
 *                         thank-you screen. Sellers are asked to reply to our email with photos.
 */
(function () {
  "use strict";

  var MAX_PHOTOS = 6;
  var MAX_EDGE = 2000; // px, longest side after downscaling
  var JPEG_QUALITY = 0.85;
  var MAX_TOTAL_BYTES = 20 * 1024 * 1024;
  var REQUEST_TIMEOUT_MS = 90000;
  var SOURCE = "chronocandy.com/sell-watch";
  var STORE_KEY = "ccsw-name";

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  function looksLikeEmail(v) { return v.indexOf("@") !== -1; }
  function isEmail(v) { return EMAIL_RE.test(v); }
  function isPhone(v) {
    if (!/^[+\d\s().\-]+$/.test(v)) return false;
    var digits = v.replace(/\D/g, "");
    return digits.length >= 7 && digits.length <= 15;
  }
  function formatBytes(n) {
    return n >= 1024 * 1024 ? (n / (1024 * 1024)).toFixed(1) + " MB" : Math.max(1, Math.round(n / 1024)) + " KB";
  }
  function esc(s) {
    return String(s).replace(/[&<>"']/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]; });
  }
  function raf(fn) { (window.requestAnimationFrame || setTimeout)(fn); }
  function storageGet(k) { try { return window.sessionStorage.getItem(k); } catch (e) { return null; } }
  function storageSet(k, v) { try { window.sessionStorage.setItem(k, v); } catch (e) { /* private mode */ } }
  function storageDel(k) { try { window.sessionStorage.removeItem(k); } catch (e) { /* private mode */ } }

  // ------------------------------------------------------------------ catalog loader (lazy)

  var catalogPromise = null;
  function loadCatalog(src) {
    if (window.CCWatchCatalog) return Promise.resolve(window.CCWatchCatalog);
    if (catalogPromise) return catalogPromise;
    catalogPromise = new Promise(function (resolve, reject) {
      if (!src) return reject(new Error("no catalog"));
      var s = document.createElement("script");
      s.src = src;
      s.async = true;
      s.onload = function () { window.CCWatchCatalog ? resolve(window.CCWatchCatalog) : reject(new Error("catalog missing")); };
      s.onerror = function () { catalogPromise = null; s.remove(); reject(new Error("catalog failed")); };
      document.head.appendChild(s);
    });
    return catalogPromise;
  }

  // ------------------------------------------------------------------ typeahead (ARIA 1.2 combobox + listbox)

  var GROUP_ORDER = ["Brand", "Model", "Reference"];

  function Typeahead(opts) {
    var input = opts.input, list = opts.list, live = opts.live, clearBtn = opts.clear;
    var listId = list.id;
    var catalog = null, open = false, active = 0, hits = [], showFree = false, text = "";

    function ensureLoaded() {
      if (catalog) return;
      loadCatalog(opts.catalogSrc).then(function (c) {
        catalog = c;
        if (document.activeElement === input) render();
      }, function () { /* free text still works */ });
    }

    function compute() {
      text = input.value.trim();
      hits = catalog && text ? catalog.search(text, 8) : [];
      hits = hits
        .map(function (h, i) { return { h: h, i: i }; })
        .sort(function (a, b) {
          var ra = GROUP_ORDER.indexOf(a.h.group), rb = GROUP_ORDER.indexOf(b.h.group);
          return ra - rb || a.i - b.i;
        })
        .map(function (x) { return x.h; });
      var exact = hits.some(function (h) { return h.label.toLowerCase() === text.toLowerCase(); });
      showFree = Boolean(text) && !exact;
    }

    function count() { return hits.length + (showFree ? 1 : 0); }
    function optId(i) { return listId + "-opt-" + i; }

    function highlighted(label, ranges) {
      var out = "", at = 0;
      ranges.forEach(function (r) {
        if (r[0] > at) out += esc(label.slice(at, r[0]));
        out += "<mark>" + esc(label.slice(r[0], r[1])) + "</mark>";
        at = r[1];
      });
      return out + esc(label.slice(at));
    }

    function render() {
      compute();
      var n = count();
      var expanded = open && n > 0;
      if (active > n - 1) active = Math.max(n - 1, 0);
      clearBtn.hidden = !input.value;
      input.setAttribute("aria-expanded", expanded ? "true" : "false");
      if (!expanded) {
        input.removeAttribute("aria-activedescendant");
        list.hidden = true;
        list.innerHTML = "";
      } else {
        var html = "", lastGroup = null, gi = 0;
        hits.forEach(function (h, i) {
          if (h.group !== lastGroup) {
            if (lastGroup !== null) html += "</div>";
            html += '<div role="group" aria-labelledby="' + listId + "-grp-" + gi + '">' +
              '<div class="ccsw-listbox__group" role="presentation" id="' + listId + "-grp-" + gi + '">' + esc(h.group) + "</div>";
            lastGroup = h.group;
            gi++;
          }
          html += '<div class="ccsw-option" role="option" id="' + optId(i) + '" data-i="' + i + '" aria-selected="' + (i === active) + '"><span>' +
            highlighted(h.label, h.ranges) + "</span></div>";
        });
        if (lastGroup !== null) html += "</div>";
        if (showFree) {
          var fi = hits.length;
          html += '<div class="ccsw-option ccsw-option--free" role="option" id="' + optId(fi) + '" data-i="' + fi + '" aria-selected="' + (fi === active) + '">' +
            "<span>Use <strong>“" + esc(text) + "”</strong></span></div>";
        }
        list.innerHTML = html;
        list.hidden = false;
        input.setAttribute("aria-activedescendant", optId(active));
        var el = document.getElementById(optId(active));
        if (el && el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
      }
      live.textContent = open && text ? (catalog ? hits.length + (hits.length === 1 ? " suggestion." : " suggestions.") : "Loading suggestions.") : "";
    }

    function setActive(i) {
      if (i === active) return;
      var prev = document.getElementById(optId(active));
      if (prev) prev.setAttribute("aria-selected", "false");
      active = i;
      var el = document.getElementById(optId(active));
      if (el) {
        el.setAttribute("aria-selected", "true");
        input.setAttribute("aria-activedescendant", optId(active));
        if (el.scrollIntoView) el.scrollIntoView({ block: "nearest" });
      }
    }

    function choose(i) {
      if (i < hits.length) {
        var hit = hits[i];
        opts.onPick(hit.value, "option");
        if (!hit.value.model) {
          // a brand: keep the list open on that brand's models
          input.value = hit.value.brand + " ";
          active = 0;
          open = true;
          input.focus();
          render();
          return;
        }
        input.value = hit.label;
      } else if (showFree) {
        freeText(text, "free");
      }
      open = false;
      render();
    }

    function freeText(t, source) {
      if (!t) return;
      loadCatalog(opts.catalogSrc).then(function (c) { opts.onPick(c.parseWatchText(t), source); }, function () {
        opts.onPick({ brand: t, model: "" }, source);
      });
    }

    input.addEventListener("input", function () { ensureLoaded(); active = 0; open = true; render(); if (opts.onInput) opts.onInput(); });
    input.addEventListener("focus", function () { ensureLoaded(); open = true; render(); });
    input.addEventListener("blur", function () {
      open = false;
      render();
      var t = input.value.trim();
      if (t && !opts.isFilled()) freeText(t, "blur");
    });
    input.addEventListener("keydown", function (e) {
      var n = count();
      if (e.key === "ArrowDown" || e.key === "Down") {
        e.preventDefault();
        ensureLoaded();
        if (!open) { open = true; render(); } else if (n) setActive((Math.min(active, n - 1) + 1) % n);
      } else if (e.key === "ArrowUp" || e.key === "Up") {
        e.preventDefault();
        if (!open) { open = true; render(); } else if (n) setActive((Math.min(active, n - 1) - 1 + n) % n);
      } else if (e.key === "Enter") {
        if (open && n) { e.preventDefault(); choose(active); }
        else if (input.value.trim()) e.preventDefault(); // never submit the form from the search box
      } else if (e.key === "Escape" || e.key === "Esc") {
        if (open && n) { e.preventDefault(); open = false; render(); }
        else if (input.value) { e.preventDefault(); input.value = ""; render(); }
      } else if (e.key === "Tab") {
        open = false;
        render();
      }
    });
    // pick on press so the input keeps focus (mouse and touch)
    list.addEventListener("mousedown", function (e) {
      var opt = e.target.closest ? e.target.closest("[role=option]") : null;
      e.preventDefault();
      if (opt) choose(Number(opt.getAttribute("data-i")));
    });
    list.addEventListener("mousemove", function (e) {
      var opt = e.target.closest ? e.target.closest("[role=option]") : null;
      if (opt) setActive(Number(opt.getAttribute("data-i")));
    });
    clearBtn.addEventListener("mousedown", function (e) {
      e.preventDefault();
      input.value = "";
      active = 0;
      open = true;
      input.focus();
      render();
    });
  }

  // ------------------------------------------------------------------ photos: downscale before upload

  function loadImage(file) {
    return new Promise(function (resolve, reject) {
      var url = URL.createObjectURL(file);
      var img = new Image();
      img.onload = function () { resolve({ img: img, url: url }); };
      img.onerror = function () { URL.revokeObjectURL(url); reject(new Error("decode")); };
      img.src = url;
    });
  }

  function downscale(file) {
    return loadImage(file).then(function (r) {
      var img = r.img;
      var w = img.naturalWidth, h = img.naturalHeight;
      var scale = Math.min(1, MAX_EDGE / Math.max(w, h));
      var cw = Math.max(1, Math.round(w * scale)), ch = Math.max(1, Math.round(h * scale));
      var canvas = document.createElement("canvas");
      canvas.width = cw;
      canvas.height = ch;
      var ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; // transparent PNGs become white, not black
      ctx.fillRect(0, 0, cw, ch);
      ctx.drawImage(img, 0, 0, cw, ch);
      URL.revokeObjectURL(r.url);
      return new Promise(function (resolve) {
        canvas.toBlob(function (blob) {
          var base = (file.name || "photo").replace(/\.[^.]+$/, "").replace(/[^\w\-]+/g, "_").slice(0, 60) || "photo";
          // keep the original when it is already a small-enough JPEG that re-encoding would only grow
          if (!blob || (scale === 1 && file.type === "image/jpeg" && file.size <= blob.size)) resolve({ blob: file, name: base + ".jpg" });
          else resolve({ blob: blob, name: base + ".jpg" });
        }, "image/jpeg", JPEG_QUALITY);
      });
    });
  }

  // ------------------------------------------------------------------ the form

  function init(root) {
    if (root.__ccsw) return;
    root.__ccsw = true;

    var $ = function (sel) { return root.querySelector(sel); };
    var $$ = function (sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); };

    var mode = root.getAttribute("data-mode") === "endpoint" ? "endpoint" : "contact";
    var endpoint = root.getAttribute("data-endpoint") || "";
    var successTpl = root.getAttribute("data-success") || "Thanks, [name]. We'll reply within one business day with an estimate.";
    var form = $("[data-ccsw-form]");
    var flow = $("[data-ccsw-flow]");
    var done = $("[data-ccsw-done]");
    var title = $("[data-ccsw-title]");
    var feedback = $("[data-ccsw-feedback]");
    var nextBtn = $("[data-ccsw-next]");
    var backBtn = $("[data-ccsw-back]");
    var details = $("[data-ccsw-details]");
    var manualRow = $("[data-ccsw-manual-row]");
    var manualBtn = $("[data-ccsw-manual]");
    var yearField = $('[data-ccsw-field="year"]');
    var searchInput = $("[data-ccsw-search]");
    var searchHint = $("[data-ccsw-search-hint]");
    var fileInput = $("[data-ccsw-file]");
    var drop = $("[data-ccsw-drop]");
    var thumbs = $("[data-ccsw-thumbs]");
    var photoStatus = $("[data-ccsw-photo-status]");
    var fallback = root.querySelector("form.ccsw-fallback");
    var el = function (name) { return form.elements[name]; };

    var step = 0;
    var revealed = false;
    var photos = []; // { blob, name, url }
    var processing = 0;
    var sending = false;

    // --- errors ---------------------------------------------------------------

    function fieldWrap(key) { return root.querySelector('[data-ccsw-field="' + key + '"]'); }
    function controlFor(key) {
      if (key === "search") return searchInput;
      var c = el(key);
      return c && c.nodeType === 1 ? c : null; // radio groups (box, papers...) have no error slot
    }
    function setError(key, msg) {
      var wrap = fieldWrap(key), ctl = controlFor(key);
      if (!wrap || !ctl) return;
      var id = ctl.id + "-error";
      var p = document.getElementById(id);
      if (!p) {
        p = document.createElement("p");
        p.className = "ccsw-error";
        p.id = id;
        wrap.appendChild(p);
      }
      p.textContent = msg;
      ctl.setAttribute("aria-invalid", "true");
      var d = (ctl.getAttribute("aria-describedby") || "").split(" ").filter(Boolean);
      if (d.indexOf(id) === -1) d.push(id);
      ctl.setAttribute("aria-describedby", d.join(" "));
    }
    function clearError(key) {
      var ctl = controlFor(key);
      if (!ctl) return;
      var id = ctl.id + "-error";
      var p = document.getElementById(id);
      if (p) p.remove();
      ctl.removeAttribute("aria-invalid");
      var d = (ctl.getAttribute("aria-describedby") || "").split(" ").filter(function (x) { return x && x !== id; });
      if (d.length) ctl.setAttribute("aria-describedby", d.join(" "));
      else ctl.removeAttribute("aria-describedby");
    }
    function clearAllErrors() {
      ["search", "brand", "model", "reference", "year", "asking_price", "name", "contact", "best_time", "consent"].forEach(clearError);
      feedback.innerHTML = "";
    }
    function focusFirstInvalid() {
      var first = root.querySelector('[data-ccsw-step="' + step + '"] [aria-invalid="true"]');
      if (first) {
        first.focus({ preventScroll: true });
        first.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }

    // --- step 1: watch -------------------------------------------------------------

    function val(name) { var e = el(name); return e ? String(e.value || "").trim() : ""; }
    function radio(name) {
      var nodes = root.querySelectorAll('input[type=radio][name="' + name + '"]');
      for (var i = 0; i < nodes.length; i++) if (nodes[i].checked) return nodes[i].value;
      return "";
    }
    function isFilled() { return Boolean(val("brand") || val("model") || val("reference")); }

    function reveal() {
      if (revealed) return;
      revealed = true;
      details.hidden = false;
      manualRow.hidden = true;
      manualBtn.hidden = true;
      root.querySelector('[data-ccsw-year-slot="details"]').appendChild(yearField);
      searchHint.hidden = true;
      clearError("search");
    }

    new Typeahead({
      input: searchInput,
      list: $("[data-ccsw-listbox]"),
      live: $("[data-ccsw-live]"),
      clear: $("[data-ccsw-clear]"),
      catalogSrc: root.getAttribute("data-catalog-src"),
      isFilled: isFilled,
      onInput: function () { clearError("search"); },
      onPick: function (p, source) {
        el("brand").value = p.brand || "";
        el("model").value = p.model || "";
        el("reference").value = p.reference || "";
        ["brand", "model", "reference"].forEach(clearError);
        reveal();
        if (source === "free" && !p.model) raf(function () { el("model").focus(); });
      },
    });

    // Pre-fill from a product page's "Sell yours" link: ?brand=&model=&ref=
    (function () {
      var q; try { q = new URLSearchParams(window.location.search); } catch (e) { return; }
      var b = (q.get("brand") || "").trim(), m = (q.get("model") || "").trim(), r = (q.get("ref") || "").trim();
      if (!b && !m && !r) return;
      el("brand").value = b; el("model").value = m; el("reference").value = r;
      searchInput.value = [b, m, r].filter(Boolean).join(" ");
      reveal();
      raf(function () { root.scrollIntoView({ block: "start" }); });
    })();

    manualBtn.addEventListener("click", function () {
      reveal();
      raf(function () { el("brand").focus(); });
    });

    el("year").addEventListener("input", function () { this.value = this.value.replace(/\D/g, "").slice(0, 4); });
    el("asking_price").addEventListener("input", function () {
      var digits = this.value.replace(/\D/g, "").replace(/^0+(?=\d)/, "").slice(0, 9);
      this.value = digits ? Number(digits).toLocaleString("en-US") : "";
    });

    // pills: mirror :checked for browsers without :has()
    function syncPills() {
      $$(".ccsw-pill").forEach(function (lab) {
        var r = lab.querySelector("input");
        lab.classList.toggle("is-checked", Boolean(r && r.checked));
      });
    }
    root.addEventListener("change", function (e) {
      if (e.target && e.target.type === "radio") syncPills();
    });
    syncPills();

    // clear a field's error as soon as it's edited
    root.addEventListener("input", function (e) {
      var t = e.target;
      if (!t || !t.name) return;
      if (t.name === "brand" || t.name === "model") clearError("search");
      clearError(t.name);
    });
    el("consent").addEventListener("change", function () { clearError("consent"); });

    function validateWatch() {
      var errs = {};
      var brand = val("brand"), model = val("model"), year = val("year");
      if (!revealed) {
        if (!brand || !model) errs.search = searchInput.value.trim() ? "Pick your watch from the list, or enter the details by hand." : "Tell us which watch it is.";
      } else {
        if (!brand) errs.brand = "Enter the brand.";
        if (!model) errs.model = "Enter the model.";
      }
      var nowYear = new Date().getFullYear();
      if (year && (!/^\d{4}$/.test(year) || Number(year) < 1900 || Number(year) > nowYear + 1)) errs.year = "Enter a 4-digit year, e.g. 2019.";
      return errs;
    }

    // --- step 2: photos -------------------------------------------------------------

    function totalBytes() { return photos.reduce(function (s, p) { return s + p.blob.size; }, 0); }
    function setStatus(msg, isError) {
      photoStatus.textContent = msg;
      photoStatus.classList.toggle("is-error", Boolean(isError));
    }
    function statusLine() {
      if (processing) return "Preparing photos…";
      if (!photos.length) return "";
      return photos.length + " of " + MAX_PHOTOS + " photos · " + formatBytes(totalBytes());
    }
    function renderThumbs() {
      thumbs.innerHTML = "";
      photos.forEach(function (p, i) {
        var li = document.createElement("li");
        li.className = "ccsw-thumb";
        li.innerHTML = '<img alt="Photo ' + (i + 1) + '" src="' + p.url + '">' +
          '<button type="button" class="ccsw-thumb__remove" data-remove="' + i + '"><span><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M18 6 6 18M6 6l12 12"/></svg></span>' +
          '<span class="ccsw-sr">Remove photo ' + (i + 1) + "</span></button>";
        thumbs.appendChild(li);
      });
      var full = photos.length >= MAX_PHOTOS;
      drop.classList.toggle("is-full", full);
      fileInput.disabled = full;
      $(".ccsw-drop__title").textContent = photos.length ? (full ? "6 photos added" : "Add more photos") : "Add photos";
    }

    function addFiles(list) {
      var files = Array.prototype.slice.call(list || []).filter(function (f) { return /^image\//.test(f.type) || /\.(heic|heif|jpe?g|png|webp)$/i.test(f.name || ""); });
      var notImages = (list ? list.length : 0) - files.length;
      var room = MAX_PHOTOS - photos.length - processing;
      var msgs = [];
      if (notImages > 0) msgs.push("Only photos can be added.");
      if (files.length > room) {
        msgs.push("You can add up to " + MAX_PHOTOS + " photos.");
        files = files.slice(0, Math.max(room, 0));
      }
      if (!files.length) { if (msgs.length) setStatus(msgs.join(" "), true); return; }
      processing += files.length;
      setStatus(statusLine());
      var failed = 0, tooBig = 0;
      files.reduce(function (chain, f) {
        return chain.then(function () {
          return downscale(f).then(function (r) {
            if (totalBytes() + r.blob.size > MAX_TOTAL_BYTES) { tooBig++; return; }
            photos.push({ blob: r.blob, name: r.name, url: URL.createObjectURL(r.blob) });
          }, function () { failed++; }).then(function () { processing--; renderThumbs(); setStatus(statusLine()); });
        });
      }, Promise.resolve()).then(function () {
        if (failed) msgs.push(failed === 1 ? "One file couldn't be opened as a photo. Try a JPG or PNG." : failed + " files couldn't be opened as photos. Try JPG or PNG.");
        if (tooBig) msgs.push("That's more than we can send at once (20 MB). Remove a photo or skip the rest.");
        if (msgs.length) setStatus((statusLine() ? statusLine() + ". " : "") + msgs.join(" "), true);
      });
    }

    fileInput.addEventListener("change", function () {
      addFiles(fileInput.files);
      fileInput.value = "";
    });
    ["dragenter", "dragover"].forEach(function (t) {
      drop.addEventListener(t, function (e) { e.preventDefault(); drop.classList.add("is-over"); });
    });
    ["dragleave", "drop"].forEach(function (t) {
      drop.addEventListener(t, function () { drop.classList.remove("is-over"); });
    });
    drop.addEventListener("drop", function (e) {
      e.preventDefault();
      if (e.dataTransfer) addFiles(e.dataTransfer.files);
    });
    thumbs.addEventListener("click", function (e) {
      var b = e.target.closest ? e.target.closest("[data-remove]") : null;
      if (!b) return;
      var i = Number(b.getAttribute("data-remove"));
      var p = photos.splice(i, 1)[0];
      if (p) URL.revokeObjectURL(p.url);
      renderThumbs();
      setStatus(statusLine() || "Photo removed.");
      var next = thumbs.querySelector('[data-remove="' + Math.min(i, photos.length - 1) + '"]');
      (next || fileInput).focus();
    });
    $("[data-ccsw-skip]").addEventListener("click", function () { go(2); });

    if (mode === "contact") {
      $("[data-ccsw-photos-upload]").hidden = true;
      $("[data-ccsw-photos-email]").hidden = false;
      var cl = $("[data-ccsw-contact-label]");
      cl.innerHTML = 'Email <span class="ccsw-req" aria-hidden="true">*</span>';
      $("[data-ccsw-contact-hint]").textContent = "We'll reply by email. You can send photos in your reply.";
      el("contact").placeholder = "you@example.com";
      el("contact").setAttribute("inputmode", "email");
      el("contact").setAttribute("autocomplete", "email");
    }

    // --- step 3: you ---------------------------------------------------------------

    el("contact").addEventListener("input", function () {
      if (mode !== "endpoint") return;
      var email = looksLikeEmail(this.value);
      this.setAttribute("inputmode", email ? "email" : "text");
      this.setAttribute("autocomplete", email ? "email" : "tel");
    });

    function validateYou() {
      var errs = {};
      var contact = val("contact");
      if (!val("name")) errs.name = "Enter your name.";
      if (mode === "contact") {
        if (!contact) errs.contact = "Enter your email so we can reply.";
        else if (!isEmail(contact)) errs.contact = "That doesn't look like an email address.";
      } else if (!contact) errs.contact = "Enter a phone number or email.";
      else if (looksLikeEmail(contact) ? !isEmail(contact) : !isPhone(contact)) errs.contact = "That doesn't look like a phone number or email.";
      if (!el("consent").checked) errs.consent = "Please tick the box so we can contact you.";
      return errs;
    }

    // --- navigation ---------------------------------------------------------------

    function show(n) {
      step = n;
      $$("[data-ccsw-step]").forEach(function (s) { s.hidden = Number(s.getAttribute("data-ccsw-step")) !== n; });
      $$("[data-ccsw-only-step]").forEach(function (s) { s.hidden = Number(s.getAttribute("data-ccsw-only-step")) !== n; });
      $$("[data-ccsw-stepbar]").forEach(function (s) {
        var i = Number(s.getAttribute("data-ccsw-stepbar"));
        if (i === n) s.setAttribute("aria-current", "step");
        else s.removeAttribute("aria-current");
        s.classList.toggle("is-done", i < n);
      });
      title.textContent = title.getAttribute("data-title-" + n) || title.textContent;
      backBtn.hidden = n === 0;
      nextBtn.textContent = n === 2 ? "Send" : "Continue";
    }

    function go(n) {
      clearAllErrors();
      show(n);
      raf(function () {
        title.focus({ preventScroll: true });
        var top = root.getBoundingClientRect().top;
        if (top < 0 || top > window.innerHeight * 0.4) root.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }

    function showErrors(errs, onStep) {
      if (onStep !== step) show(onStep);
      Object.keys(errs).forEach(function (k) { setError(k, errs[k]); });
      focusFirstInvalid();
    }

    backBtn.addEventListener("click", function () { if (!sending) go(Math.max(step - 1, 0)); });

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      if (sending) return;
      clearAllErrors();
      if (step === 0) {
        var w = validateWatch();
        if (Object.keys(w).length) return showErrors(w, 0);
        return go(1);
      }
      if (step === 1) {
        if (processing) { setStatus("Preparing photos… one moment."); return; }
        return go(2);
      }
      submit(false);
    });

    // --- submit -------------------------------------------------------------------

    function fields() {
      var brand = val("brand"), model = val("model");
      var contact = val("contact");
      return {
        intent: radio("intent") || "Sell outright",
        brand: brand,
        model: model,
        reference: val("reference"),
        year: val("year"),
        box: radio("box"),
        papers: radio("papers"),
        condition: radio("condition"),
        asking_price: val("asking_price") ? "$" + val("asking_price") : "",
        name: val("name"),
        contact: contact,
        best_time: val("best_time"),
        consent: el("consent").checked ? "Yes" : "No",
        source: SOURCE,
        _subject: ("Sell request: " + brand + " " + model).trim(),
      };
    }

    function setSending(on, label) {
      sending = on;
      nextBtn.disabled = on;
      backBtn.disabled = on;
      form.setAttribute("aria-busy", on ? "true" : "false");
      nextBtn.innerHTML = on ? '<span class="ccsw-spin" aria-hidden="true"></span><span>' + esc(label || "Sending…") + "</span>" : "Send";
    }

    function showFailure(msg, offerNoPhotos) {
      var html = '<div class="ccsw-alert" role="alert"><p>' + esc(msg) + "</p>";
      if (offerNoPhotos) html += '<button type="button" class="ccsw-link" data-ccsw-nophotos>Send without photos</button>';
      html += "</div>";
      feedback.innerHTML = html;
      var b = feedback.querySelector("[data-ccsw-nophotos]");
      if (b) b.addEventListener("click", function () { submit(true); });
    }

    function firstName(name) { return (name || "").trim().split(/\s+/)[0] || ""; }

    function showDone(name, summary, emailPhotos) {
      var first = firstName(name);
      var msg = successTpl.replace(/\[name\]/g, first);
      if (!first) msg = msg.replace(/^Thanks,\s*\./, "Thanks.").replace(/,\s*\./g, ".");
      $("[data-ccsw-done-title]").textContent = msg;
      var sum = $("[data-ccsw-done-summary]");
      sum.textContent = summary || "";
      sum.hidden = !summary;
      $("[data-ccsw-done-email]").hidden = !emailPhotos;
      flow.hidden = true;
      done.hidden = false;
      raf(function () {
        $("[data-ccsw-done-title]").focus({ preventScroll: true });
        root.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    }

    function summaryText(f) {
      var watch = [f.brand, f.model, f.reference].filter(Boolean).join(" ");
      return watch ? "Request received for your " + watch + "." : "";
    }

    function submit(withoutPhotos) {
      var w = validateWatch();
      if (Object.keys(w).length) return showErrors(w, 0);
      var y = validateYou();
      if (Object.keys(y).length) return showErrors(y, 2);
      var f = fields();

      // honeypot: bots fill hidden fields. Pretend it worked and send nothing.
      if (el("_gotcha") && el("_gotcha").value) return showDone(f.name, summaryText(f), false);

      if (mode === "contact") return submitContact(f);

      var data = new FormData();
      Object.keys(f).forEach(function (k) { if (f[k]) data.append(k, f[k]); });
      if (isEmail(f.contact)) data.append("email", f.contact); // lets the inbox "Reply" go straight to the seller
      else data.append("phone", f.contact);
      data.append("_gotcha", "");
      if (/formsubmit\.co/i.test(endpoint)) {
        // FormSubmit: no redirect captcha, tidy table email, reply goes to the seller, its own honeypot field
        data.append("_captcha", "false");
        data.append("_template", "table");
        data.append("_honey", "");
        if (isEmail(f.contact)) data.append("_replyto", f.contact);
      }
      var sendPhotos = withoutPhotos ? [] : photos;
      sendPhotos.forEach(function (p, i) { data.append("photo_" + (i + 1), p.blob, p.name); });
      if (withoutPhotos && photos.length) data.append("photos_note", "Seller has " + photos.length + " photo(s); they didn't upload, please ask for them.");

      setSending(true, sendPhotos.length ? "Sending photos…" : "Sending…");
      var ctrl = window.AbortController ? new AbortController() : null;
      var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, REQUEST_TIMEOUT_MS) : null;

      // FormSubmit only keeps attachments on its standard (non-AJAX) endpoint. That endpoint doesn't allow
      // cross-site reads, so send with no-cors and treat the opaque response as delivered.
      var isFormSubmit = /formsubmit\.co/i.test(endpoint);
      if (isFormSubmit) {
        return fetch(endpoint.replace(/\/ajax\//i, "/"), { method: "POST", body: data, mode: "no-cors", signal: ctrl ? ctrl.signal : undefined })
          .then(function () { clearTimeout(timer); setSending(false); showDone(f.name, summaryText(f), false); })
          .catch(function () {
            clearTimeout(timer); setSending(false);
            showFailure("We couldn't reach our server. Check your connection and tap Send again. Nothing you entered has been lost.", sendPhotos.length > 0);
          });
      }
      fetch(endpoint, { method: "POST", body: data, headers: { Accept: "application/json" }, signal: ctrl ? ctrl.signal : undefined })
        .then(function (res) {
          return res.json().catch(function () { return {}; }).then(function (body) { return { res: res, body: body }; });
        })
        .then(function (r) {
          clearTimeout(timer);
          setSending(false);
          // Formspree answers {ok:true}; FormSubmit answers {success:"true"|"false", message}
          var failed = r.body.ok === false || r.body.success === false || r.body.success === "false";
          if (r.res.ok && !failed) return showDone(f.name, summaryText(f), false);
          var hasPhotos = sendPhotos.length > 0;
          var tooLarge = r.res.status === 413 || /file|upload|size|large/i.test(JSON.stringify(r.body.errors || r.body.error || ""));
          if (hasPhotos && tooLarge) return showFailure("We couldn't send your photos. Try removing a few, or send your details now and we'll ask for photos when we reply.", true);
          showFailure("Sorry, that didn't go through. Please try again in a moment." + (hasPhotos ? " You can also send it without photos." : ""), hasPhotos);
        })
        .catch(function () {
          clearTimeout(timer);
          setSending(false);
          showFailure("We couldn't reach our server. Check your connection and tap Send again. Nothing you entered has been lost.", sendPhotos.length > 0);
        });
    }

    function submitContact(f) {
      if (!fallback) {
        showFailure("This form isn't set up yet. Please contact us directly.", false);
        return;
      }
      var lines = [
        f._subject,
        "",
        "I'd like to: " + f.intent,
        "Brand: " + f.brand,
        "Model: " + f.model,
        "Reference: " + (f.reference || "-"),
        "Approx. year: " + (f.year || "-"),
        "Original box: " + (f.box || "-"),
        "Papers: " + (f.papers || "-"),
        "Condition: " + (f.condition || "-"),
        "Asking price: " + (f.asking_price || "-"),
        "",
        "Name: " + f.name,
        "Email: " + f.contact,
        "Best time to reach: " + (f.best_time || "-"),
        "Agreed to be contacted: " + f.consent,
        "Photos: seller will reply by email with photos",
        "Source: " + f.source,
      ];
      fallback.querySelector('[name="contact[name]"]').value = f.name;
      fallback.querySelector('[name="contact[email]"]').value = f.contact;
      fallback.querySelector('[name="contact[body]"]').value = lines.join("\n");
      storageSet(STORE_KEY, JSON.stringify({ name: f.name, summary: summaryText(f) }));
      setSending(true);
      if (typeof fallback.requestSubmit === "function") fallback.requestSubmit();
      else fallback.submit();
    }

    // back from Shopify's contact form post (page reloaded)
    if (mode === "contact" && root.querySelector("[data-ccsw-posted]")) {
      var saved = {};
      try { saved = JSON.parse(storageGet(STORE_KEY) || "{}") || {}; } catch (e) { saved = {}; }
      storageDel(STORE_KEY);
      showDone(saved.name || "", saved.summary || "", true);
    } else if (mode === "contact" && root.querySelector("[data-ccsw-post-error]")) {
      show(0);
      showFailure("Sorry, that didn't go through. Please check your email address and try again.", false);
    } else {
      show(0);
    }
  }

  function boot() {
    Array.prototype.forEach.call(document.querySelectorAll(".ccsw[data-ccsw]"), init);
  }
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
  // theme editor: re-init when the section is added or re-rendered
  document.addEventListener("shopify:section:load", boot);
})();
